import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, doc, updateDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, TextField,
  Button, IconButton, Alert, CircularProgress,
  Chip, Divider, Select, MenuItem, FormControl,
  InputLabel, Dialog, DialogTitle, DialogContent,
  DialogActions, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RateReviewIcon from '@mui/icons-material/RateReview';
import EmailIcon from '@mui/icons-material/Email';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate } from 'react-router-dom';
import { FEEDBACK_BLOCKS } from './FeedbackPage';
import { authFetch } from '../utils/authFetch';

const STATUS_CONFIG = {
  ny_kommentar: {
    label: 'Ny kommentar', color: '#F06292', text: 'white'
  },
  avvisad: {
    label: 'Avvisad', color: '#78909C', text: 'white'
  },
  har_inarbetats: {
    label: 'Har åtgärdats', color: '#66BB6A', text: 'white'
  },
  annat_forum: {
    label: 'Annat forum eller skede',
    color: '#283593', text: 'white'
  },
  ny_granskning: {
    label: 'Ny granskning', color: '#FFA726', text: 'white'
  },
  ska_inarbetas: {
    label: 'Ska åtgärdas', color: '#EF5350', text: 'white'
  },
};

export default function FeedbackAdmin() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterBlock, setFilterBlock] = useState('all');
  const [dialog, setDialog] = useState({
    open: false, item: null, status: '', comment: ''
  });
  const [saving, setSaving] = useState(false);
  const [sendingMail, setSendingMail] = useState(false);
  const [mailSent, setMailSent] = useState(false);
  const [mailCompose, setMailCompose] = useState({ open: false, subject: '', body: '' });

  useEffect(() => {
    loadFeedback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadFeedback() {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'feedback'));
      const items = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) =>
          (b.createdAt?.toDate?.() || 0) -
          (a.createdAt?.toDate?.() || 0)
        );
      setFeedback(items);
    } catch (err) {
      console.error(err);
      setError('Kunde inte ladda feedback.');
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveResponse() {
    if (!dialog.item) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'feedback', dialog.item.id), {
        status: dialog.status,
        adminComment: dialog.comment,
        updatedAt: new Date(),
        respondedBy: currentUser.uid,
      });
      setSaved('Svar sparat!');
      setTimeout(() => setSaved(''), 3000);
      setDialog({
        open: false, item: null, status: '', comment: ''
      });
      loadFeedback();
    } catch (err) {
      console.error(err);
      setError('Kunde inte spara svar.');
    } finally {
      setSaving(false);
    }
  }

  function handleOpenMailCompose() {
    if (!dialog.item || !dialog.comment.trim()) return;
    setMailCompose({
      open: true,
      subject: `Svar på din feedback: ${dialog.item.title}`,
      body: dialog.comment,
    });
  }

  async function handleSendMailNow() {
    const recipientEmail = dialog.item?.submittedByEmail;
    if (!recipientEmail) {
      setError('Ingen e-postadress registrerad för denna feedback.');
      return;
    }
    setSendingMail(true);
    try {
      await updateDoc(doc(db, 'feedback', dialog.item.id), {
        status: dialog.status,
        adminComment: dialog.comment,
        updatedAt: new Date(),
        respondedBy: currentUser.uid,
      });
      const res = await authFetch('/.netlify/functions/feedback-reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: recipientEmail,
          toName: dialog.item.submittedByName || '',
          feedbackTitle: dialog.item.title,
          feedbackDescription: dialog.item.description || '',
          adminComment: mailCompose.body,
          statusLabel: STATUS_CONFIG[dialog.status]?.label || dialog.status,
          respondedByName: currentUser.displayName || '',
          subject: mailCompose.subject,
        }),
      });
      if (!res.ok) throw new Error('Serverfel');
      setMailSent(true);
      setMailCompose({ open: false, subject: '', body: '' });
      setSaved('Svar skickat via e-post!');
      setTimeout(() => setSaved(''), 4000);
      loadFeedback();
      setTimeout(() => setDialog({ open: false, item: null, status: '', comment: '' }), 1500);
    } catch (err) {
      console.error(err);
      setError('Kunde inte skicka e-post. Försök igen.');
    } finally {
      setSendingMail(false);
    }
  }

  function openDialog(item) {
    setMailSent(false);
    setMailCompose({ open: false, subject: '', body: '' });
    setDialog({
      open: true,
      item,
      status: item.status || 'ny_kommentar',
      comment: item.adminComment || ''
    });
  }

  const filtered = feedback.filter(item => {
    if (filterStatus !== 'all' &&
      (item.status || 'ny_kommentar') !== filterStatus)
      return false;
    if (filterType !== 'all' && item.type !== filterType)
      return false;
    if (filterBlock !== 'all' && (item.block || 'allmant') !== filterBlock)
      return false;
    return true;
  });

  const counts = {
    ny_kommentar: feedback.filter(
      f => !f.status || f.status === 'ny_kommentar').length,
    ska_inarbetas: feedback.filter(
      f => f.status === 'ska_inarbetas').length,
    ny_granskning: feedback.filter(
      f => f.status === 'ny_granskning').length,
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box sx={{
        backgroundColor: '#1a1a1a', color: 'white', py: 2, px: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton color="inherit"
            onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <RateReviewIcon />
          <Typography variant="h5" fontWeight="bold">
            Feedback-hantering
          </Typography>
          {counts.ny_kommentar > 0 && (
            <Chip
              label={counts.ny_kommentar + ' nya'}
              size="small"
              sx={{ bgcolor: '#EC407A', color: 'white',
                fontSize: '0.7rem' }}
            />
          )}
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        {saved && (
          <Alert severity="success" sx={{ mb: 2 }}>{saved}</Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Statistik-översikt */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3,
          flexWrap: 'wrap' }}>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const count = feedback.filter(
              f => (f.status || 'ny_kommentar') === key).length;
            if (count === 0) return null;
            return (
              <Paper key={key} sx={{
                px: 2, py: 1.5, cursor: 'pointer',
                border: '2px solid',
                borderColor: filterStatus === key
                  ? cfg.color : 'transparent',
                transition: 'all 0.2s',
                '&:hover': { borderColor: cfg.color }
              }}
                onClick={() => setFilterStatus(
                  filterStatus === key ? 'all' : key)}>
                <Box sx={{ display: 'flex', alignItems: 'center',
                  gap: 1 }}>
                  <Box sx={{
                    width: 12, height: 12, borderRadius: '50%',
                    bgcolor: cfg.color
                  }} />
                  <Typography variant="caption" fontWeight="bold">
                    {cfg.label}
                  </Typography>
                  <Chip label={count} size="small"
                    sx={{ height: 18, fontSize: '0.65rem',
                      bgcolor: cfg.color, color: cfg.text }} />
                </Box>
              </Paper>
            );
          })}
        </Box>

        {/* Filter */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center',
            gap: 2, flexWrap: 'wrap' }}>
            <FilterListIcon sx={{ color: 'text.secondary' }} />
            <ToggleButtonGroup value={filterType} exclusive
              size="small"
              onChange={(e, val) => {
                if (val !== null) setFilterType(val);
              }}>
              <ToggleButton value="all">Alla</ToggleButton>
              <ToggleButton value="feature">
                <LightbulbIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Önskemål
              </ToggleButton>
              <ToggleButton value="bug">
                <BugReportIcon sx={{ mr: 0.5, fontSize: 16 }} />
                Felrapporter
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup value={filterStatus} exclusive
              size="small"
              onChange={(e, val) => {
                if (val !== null) setFilterStatus(val);
              }}>
              <ToggleButton value="all">Alla statusar</ToggleButton>
              <ToggleButton value="ny_kommentar">
                Ohanterade
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Block</InputLabel>
              <Select value={filterBlock} label="Block"
                onChange={e => setFilterBlock(e.target.value)}>
                <MenuItem value="all">Alla block</MenuItem>
                {FEEDBACK_BLOCKS.map(b => (
                  <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary"
              sx={{ ml: 'auto' }}>
              {filtered.length} av {feedback.length} visas
            </Typography>
          </Box>
        </Paper>

        {/* Lista */}
        {filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Ingen feedback matchar filtret.
            </Typography>
          </Paper>
        ) : (
          filtered.map(item => {
            const status = STATUS_CONFIG[
              item.status || 'ny_kommentar'];
            return (
              <Paper key={item.id}
                sx={{ mb: 2, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex',
                  alignItems: 'stretch' }}>

                  {/* Statusindikator vänster */}
                  <Box sx={{
                    width: 6, bgcolor: status.color,
                    flexShrink: 0
                  }} />

                  <Box sx={{ flex: 1, p: 2.5 }}>
                    <Box sx={{ display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex',
                          alignItems: 'center', gap: 1,
                          mb: 0.5 }}>
                          {item.type === 'bug'
                            ? <BugReportIcon sx={{
                                fontSize: 18, color: '#EF5350'
                              }} />
                            : <LightbulbIcon sx={{
                                fontSize: 18, color: '#FFA726'
                              }} />
                          }
                          <Typography variant="subtitle1"
                            fontWeight="bold">
                            {item.title}
                          </Typography>
                          <Chip
                            label={item.type === 'bug'
                              ? 'Felrapport' : 'Önskemål'}
                            size="small"
                            sx={{ height: 18,
                              fontSize: '0.6rem',
                              bgcolor: item.type === 'bug'
                                ? '#ffebee' : '#fff8e1',
                              color: item.type === 'bug'
                                ? '#c62828' : '#e65100'
                            }}
                          />
                          {item.block && item.block !== 'allmant' && (
                            <Chip
                              label={FEEDBACK_BLOCKS.find(b => b.value === item.block)?.label || item.block}
                              size="small"
                              sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#ede7f6', color: '#4527a0' }}
                            />
                          )}
                        </Box>

                        <Typography variant="body2"
                          color="text.secondary" sx={{ mb: 1 }}>
                          {item.description}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2,
                          flexWrap: 'wrap' }}>
                          <Typography variant="caption"
                            color="text.secondary">
                            👤 {item.submittedByName ||
                              item.submittedByEmail}
                          </Typography>
                          <Typography variant="caption"
                            color="text.secondary">
                            📅 {item.createdAt?.toDate?.()
                              ? item.createdAt.toDate()
                                  .toLocaleDateString('sv-SE')
                              : ''}
                          </Typography>
                        </Box>

                        {item.adminComment && (
                          <Box sx={{
                            mt: 1.5, p: 1.5,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            borderLeft: '3px solid',
                            borderColor: status.color
                          }}>
                            <Typography variant="caption"
                              fontWeight="bold"
                              color="text.secondary">
                              Ditt svar:
                            </Typography>
                            <Typography variant="body2"
                              sx={{ mt: 0.3 }}>
                              {item.adminComment}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end', gap: 1 }}>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            bgcolor: status.color,
                            color: status.text,
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                          }}
                        />
                        <Button size="small" variant="outlined"
                          onClick={() => openDialog(item)}>
                          Svara / Uppdatera
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            );
          })
        )}
      </Container>

      {/* Svar-dialog */}
      <Dialog open={dialog.open}
        onClose={() => setDialog({
          open: false, item: null, status: '', comment: ''
        })}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center',
            gap: 1 }}>
            {dialog.item?.type === 'bug'
              ? <BugReportIcon sx={{ color: '#EF5350' }} />
              : <LightbulbIcon sx={{ color: '#FFA726' }} />
            }
            {dialog.item?.title}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary"
            sx={{ mb: 2 }}>
            {dialog.item?.description}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Inskickat av {dialog.item?.submittedByName ||
                dialog.item?.submittedByEmail}
            </Typography>
            {dialog.item?.block && dialog.item.block !== 'allmant' && (
              <Chip
                label={FEEDBACK_BLOCKS.find(b => b.value === dialog.item.block)?.label || dialog.item.block}
                size="small"
                sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#ede7f6', color: '#4527a0' }}
              />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select value={dialog.status} label="Status"
              onChange={e => setDialog(prev => ({
                ...prev, status: e.target.value
              }))}>
              {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>
                  <Box sx={{ display: 'flex',
                    alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 12, height: 12,
                      borderRadius: '50%', bgcolor: cfg.color
                    }} />
                    {cfg.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {dialog.status && (
            <Box sx={{
              mb: 2, p: 1.5, borderRadius: 1,
              bgcolor: STATUS_CONFIG[dialog.status]?.color,
              display: 'flex', alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Typography variant="body2" fontWeight="bold"
                sx={{ color: 'white' }}>
                {STATUS_CONFIG[dialog.status]?.label}
              </Typography>
            </Box>
          )}

          <TextField fullWidth multiline rows={3}
            label="Kommentar till anvandaren (valfri)"
            value={dialog.comment}
            onChange={e => {
              setDialog(prev => ({ ...prev, comment: e.target.value }));
              if (mailCompose.open) setMailCompose(prev => ({ ...prev, body: e.target.value }));
            }}
            placeholder="Forklara statusen eller ge mer information..."
          />

          {mailCompose.open && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="caption" fontWeight="bold" sx={{ letterSpacing: 1, display: 'block', mb: 1.5 }}>
                FÖRHANDSGRANSKA OCH REDIGERA MAILET
              </Typography>
              <TextField
                fullWidth size="small" label="Ämnesrad" sx={{ mb: 1.5 }}
                value={mailCompose.subject}
                onChange={e => setMailCompose(prev => ({ ...prev, subject: e.target.value }))}
              />
              <TextField
                fullWidth multiline rows={5} size="small" label="Brödtext"
                value={mailCompose.body}
                onChange={e => setMailCompose(prev => ({ ...prev, body: e.target.value }))}
                helperText={`Till: ${dialog.item?.submittedByEmail || '–'}`}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button onClick={() => setDialog({
            open: false, item: null, status: '', comment: ''
          })}>
            Avbryt
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
              onClick={handleSaveResponse}>
              {saving ? 'Sparar...' : 'Spara internt'}
            </Button>
            {!mailCompose.open ? (
              <Button variant="contained"
                disabled={!dialog.comment?.trim() || mailSent}
                startIcon={<EmailIcon />}
                onClick={handleOpenMailCompose}
                sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
                {mailSent ? 'Skickat!' : 'Skicka via e-post'}
              </Button>
            ) : (
              <Button variant="contained"
                disabled={sendingMail || !mailCompose.subject.trim() || !mailCompose.body.trim()}
                startIcon={sendingMail
                  ? <CircularProgress size={16} color="inherit" />
                  : <EmailIcon />}
                onClick={handleSendMailNow}
                sx={{ bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' } }}>
                {sendingMail ? 'Skickar...' : 'Skicka nu'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}